import { useState, useEffect } from "react";
import { Plus, Users, Loader2, Trash2, Edit3, Copy } from "lucide-react";
import { colors, fonts, CONTACT_TYPES } from "../styles/theme";
import { useAuth } from "../hooks/useAuth";
import { Button, Badge } from "../components/ui";
import AudienceListBuilder from "../components/AudienceListBuilder";
import {
  fetchAudienceLists,
  deleteAudienceList,
  createAudienceList,
  resolveAudienceList,
} from "../services/api";

const TIER_LABELS = { hot: "Hot", warm: "Warm", cool: "Cool", cold: "Cold" };

function summarizeFilters(filters) {
  if (!filters) return "No filters";
  const parts = [];
  if (filters.contactTypes?.length) {
    parts.push(filters.contactTypes.map((t) => CONTACT_TYPES[t]?.label || t).join(", "));
  }
  if (filters.engagementTiers?.length) {
    parts.push(filters.engagementTiers.map((t) => TIER_LABELS[t] || t).join(", "));
  }
  if (filters.tags?.length) {
    parts.push(`Tags: ${filters.tags.join(", ")}`);
  }
  if (filters.companies?.length) {
    parts.push(`Companies: ${filters.companies.join(", ")}`);
  }
  if (filters.countries?.length) {
    parts.push(`Countries: ${filters.countries.join(", ")}`);
  }
  if (filters.sectors?.length) {
    parts.push(`Sectors: ${filters.sectors.join(", ")}`);
  }
  if (filters.isDecisionMaker === true) parts.push("Decision Makers");
  if (filters.emailFrequency) parts.push(`Freq: ${filters.emailFrequency}`);
  if (filters.searchText) parts.push(`Search: "${filters.searchText}"`);
  if (filters.includeEmails?.length) parts.push(`+${filters.includeEmails.length} included`);
  if (filters.excludeEmails?.length) parts.push(`-${filters.excludeEmails.length} excluded`);
  return parts.length > 0 ? parts.join(" / ") : "All contacts";
}

export default function AudienceLists() {
  const { role, user } = useAuth();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [counts, setCounts] = useState({});
  const [deleting, setDeleting] = useState(null);
  const [cloning, setCloning] = useState(null);
  const [filter, setFilter] = useState("all"); // "all" | "mine" | "shared"

  const currentUser = user?.userDetails || "";

  const loadLists = () => {
    setLoading(true);
    fetchAudienceLists()
      .then((data) => {
        setLists(data.lists || []);
        (data.lists || []).forEach((l) => {
          resolveAudienceList(l.id, { preview: true })
            .then((r) => setCounts((prev) => ({ ...prev, [l.id]: r.total })))
            .catch(() => {});
        });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadLists(); }, []);

  const isOwner = (list) =>
    list.createdBy && list.createdBy.toLowerCase() === currentUser.toLowerCase();

  const isSharedWithMe = (list) =>
    (list.sharedWith || []).some((e) => e.toLowerCase() === currentUser.toLowerCase());

  const filteredLists =
    filter === "mine"
      ? lists.filter(isOwner)
      : filter === "shared"
        ? lists.filter(isSharedWithMe)
        : lists;

  const myCount = lists.filter(isOwner).length;
  const sharedCount = lists.filter(isSharedWithMe).length;

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete audience list "${name}"? This cannot be undone.`)) return;
    setDeleting(id);
    try {
      await deleteAudienceList(id);
      setLists((prev) => prev.filter((l) => l.id !== id));
    } catch (err) {
      alert(`Delete failed: ${err.message}`);
    } finally {
      setDeleting(null);
    }
  };

  const handleClone = async (list) => {
    setCloning(list.id);
    try {
      const { list: cloned } = await createAudienceList({
        name: `Copy of ${list.name}`,
        description: list.description || "",
        filters: { ...list.filters },
        excludeSuppressed: list.excludeSuppressed !== false,
        sharedWith: [],
      });
      setLists((prev) => [cloned, ...prev]);
      // Resolve count for the new list
      resolveAudienceList(cloned.id, { preview: true })
        .then((r) => setCounts((prev) => ({ ...prev, [cloned.id]: r.total })))
        .catch(() => {});
    } catch (err) {
      alert(`Clone failed: ${err.message}`);
    } finally {
      setCloning(null);
    }
  };

  const handleSaved = () => {
    setShowBuilder(false);
    setEditingList(null);
    loadLists();
  };

  const canDelete = (list) => role === "admin" || isOwner(list);

  const tabs = [
    { key: "all", label: `All Lists (${lists.length})` },
    { key: "mine", label: `My Lists (${myCount})` },
    ...(sharedCount > 0 ? [{ key: "shared", label: `Shared with Me (${sharedCount})` }] : []),
  ];

  return (
    <div style={{ padding: "32px 40px", maxWidth: 900 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: fonts.heading, fontSize: 24, color: colors.text, margin: 0 }}>
            Audience Lists
          </h1>
          <p style={{ fontSize: 13, color: colors.textSecondary, margin: "4px 0 0" }}>
            Create and manage reusable contact segments for newsletters and webcast invitations
          </p>
        </div>
        <Button variant="primary" size="sm" icon={Plus} onClick={() => { setEditingList(null); setShowBuilder(true); }}>
          New List
        </Button>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            style={{
              padding: "5px 14px", borderRadius: 6, fontSize: 12, fontWeight: 500,
              fontFamily: fonts.body, cursor: "pointer", border: "none",
              background: filter === t.key ? colors.accent : colors.bgCard,
              color: filter === t.key ? "#fff" : colors.textSecondary,
              transition: "all 0.15s",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ padding: "10px 14px", marginBottom: 16, borderRadius: 6, background: "rgba(239,68,68,0.1)", color: colors.danger, fontSize: 12 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 40, justifyContent: "center", color: colors.textSecondary }}>
          <Loader2 size={16} className="spin" /> Loading...
        </div>
      ) : filteredLists.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "60px 20px",
          background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 10,
        }}>
          <Users size={36} color={colors.textSecondary} style={{ marginBottom: 12 }} />
          <p style={{ fontSize: 14, color: colors.textSecondary, margin: "0 0 4px" }}>
            {filter === "mine" ? "You haven't created any lists yet" : filter === "shared" ? "No lists have been shared with you" : "No audience lists yet"}
          </p>
          <p style={{ fontSize: 12, color: colors.textSecondary, margin: "0 0 16px" }}>
            {filter === "mine" ? "Switch to All Lists to see lists created by others" : filter === "shared" ? "Ask a colleague to share a list with you" : "Create your first list to segment contacts for targeted emails"}
          </p>
          {filter !== "shared" && (
            <Button variant="primary" size="sm" icon={Plus} onClick={() => setShowBuilder(true)}>
              Create List
            </Button>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filteredLists.map((list) => (
            <div
              key={list.id}
              style={{
                background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 8,
                padding: "16px 20px", display: "flex", alignItems: "center", gap: 16,
                cursor: "pointer", transition: "border-color 0.15s",
              }}
              onClick={() => { setEditingList(list); setShowBuilder(true); }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = colors.accent)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = colors.border)}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: colors.text, fontFamily: fonts.body }}>
                    {list.name}
                  </span>
                  <Badge variant="default" style={{ fontSize: 10, fontWeight: 700 }}>
                    {counts[list.id] !== undefined ? counts[list.id].toLocaleString() : "..."} contacts
                  </Badge>
                  {list.sharedWith?.length > 0 && (
                    <span
                      title={`Shared with: ${list.sharedWith.join(", ")}`}
                      style={{
                        fontSize: 10,
                        color: colors.accent,
                        fontWeight: 600,
                      }}
                    >
                      Shared ({list.sharedWith.length})
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: colors.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {summarizeFilters(list.filters)}
                </div>
                {list.description && (
                  <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 3 }}>
                    {list.description}
                  </div>
                )}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <span style={{ fontSize: 10, color: colors.textSecondary, textAlign: "right" }}>
                  {list.createdBy && <span>{list.createdBy.split("@")[0]}</span>}
                  {list.createdBy && list.updatedAt && <span> &middot; </span>}
                  {list.updatedAt ? new Date(list.updatedAt).toLocaleDateString("en-GB") : ""}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); handleClone(list); }}
                  disabled={cloning === list.id}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}
                  title="Clone"
                >
                  {cloning === list.id
                    ? <Loader2 size={14} color={colors.textSecondary} className="spin" />
                    : <Copy size={14} color={colors.textSecondary} />}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setEditingList(list); setShowBuilder(true); }}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}
                  title="Edit"
                >
                  <Edit3 size={14} color={colors.textSecondary} />
                </button>
                {canDelete(list) && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(list.id, list.name); }}
                    disabled={deleting === list.id}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}
                    title="Delete"
                  >
                    {deleting === list.id
                      ? <Loader2 size={14} color={colors.textSecondary} className="spin" />
                      : <Trash2 size={14} color={colors.danger} />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showBuilder && (
        <AudienceListBuilder
          list={editingList}
          onClose={() => { setShowBuilder(false); setEditingList(null); }}
          onSaved={handleSaved}
        />
      )}    </div>
  );
}
